import { attachIndexFiltersBridge } from "#ce/apps/SpAdmin/SpIndexFilters/legacy/index-filters-to-index-table";
import {
  announceFilterChange,
  clearSelectFilter,
  isDirectionalSelection,
  isSelectFilterActive,
  selectedItemTitle,
  selectFilterDescription,
} from "#/admin/legacy/index-table/select-filters.js";
import { withReadySelects } from "#/admin/legacy/index-table/with-ready-selects.js";

class IndexTableManager {
  constructor(el) {
    this.selectors = {
      primaryIndexFilterContainer: "[data-behaviour~=primary-index-filter-container]",
      primaryIndexFilterLink: "[data-primary-index-filter]",
      primaryIndexFilterInput: "[data-primary-index-filter-input]",

      appliedFiltersSummary: "[data-index-table-applied-filters-summary]",
      filter: "[data-index-table-filter]", // Selector for an input that contains one of the filters

      focusControl: "[data-behaviour~=focus-control]", // Focus control input
      searchInput: "[data-behaviour~=admin-search] .query_input", // Search input

      paginationLink: "[data-behaviour~=index-table-row-container] .pagination a[data-remote]", // Replaced with the rows

      sortMenu: "[data-behaviour~=sort-menu]", // Location of sort menu
      sortInput: "[data-sort-filter]", // Sort filter input
      sortDirectionInput: "[data-sort-direction]", // Direction of a sort offered in both directions
      sortDirectionContainer: "[data-sort-direction-container]",
      sortText: ".button-text", // Text of the sort menu button

      bulkSelectReturnToInput: "[data-behaviour~=bulk-select-return-to]",
    };

    this.el = el;
    this.searchFilter = new IndexTableTextFilter(this.searchInput);
    attachIndexFiltersBridge(this);

    // INIT

    withReadySelects(this.el[0], this.handleSelectsReady.bind(this));

    // BEHAVIOUR

    this.el.on("click", this.selectors.primaryIndexFilterLink, this.handlePrimaryIndexFilterLinkClick.bind(this));

    this.focusControl.on("change", this.handleFocusModeChange.bind(this));

    this.sortMenu.on("change", this.selectors.filter, this.refresh.bind(this));

    // A pagination link is a remote link that jquery_ujs requests itself, so its page does not arrive through
    // `refreshTableViaAjax`, which runs `afterAjaxRefresh`. The response replaces the link, after which its events no
    // longer reach the document, so `afterAjaxRefresh` is attached when the request is sent.
    jQuery(document).on(
      "ajax:beforeSend.indexTableManager",
      this.selectors.paginationLink,
      this.handlePaginationRequest.bind(this),
    );

    jQuery(window).on("popstate", this.reloadFromUrl.bind(this));
    jQuery(window).on("pageshow", this.refreshUIOnPageShow.bind(this));
  }

  // EVENT HANDLERS

  handleSelectsReady() {
    this.refreshControls();
    this.refreshAppliedFiltersList();
    document.dispatchEvent(new CustomEvent("indexTable:refreshed"));
  }

  handlePrimaryIndexFilterLinkClick(e) {
    this.primaryIndexFilterInput.val(jQuery(e.currentTarget).data("primary-index-filter"));
    this.refresh();
  }

  handleFocusModeChange(e) {
    this.refreshTable();
  }

  handlePaginationRequest(_e, xhr) {
    xhr.done(this.afterAjaxRefresh.bind(this));
  }

  // HELPERS

  refreshUIOnPageShow() {
    window.loadingIndicator.hide();
    jQuery("span[id^='breadcrumb_initial'").show();
    jQuery("span[id^='breadcrumb_change'").remove();
    this.appliedFiltersSummary.show();
  }

  refresh() {
    this.refreshControls();
    this.refreshAppliedFiltersList();
    this.refreshTable();
  }

  refreshControls() {
    this.refreshFocusModeControl();
    this.refreshSortMenu();
  }

  refreshFocusModeControl() {
    // With no filter in the URL there is nothing to derive the state from, so leave the server-rendered value alone. It
    // reflects the focus_mode cookie, which the server applies on its own, and unchecking it here would misreport a
    // filtered table as unfiltered.
    if (this.focusParamValue === null) {
      return;
    }

    this.focusControl.prop("checked", this.focusControlCheckedValue == this.focusParamValue);
  }

  // Shows the selected sort in the button, with its direction when it is offered in both directions, and shows the
  // direction select only for such a sort.
  refreshSortMenu() {
    const directional = isDirectionalSelection(this.sortInput[0]);
    let text = selectedItemTitle(this.sortInput[0]);

    if (directional) {
      text = `${text} (${selectedItemTitle(this.sortDirectionInput[0])})`;
    }

    this.sortDirectionContainer.toggle(directional);
    this.sortText.text(text);
  }

  refreshAppliedFiltersList(state = "initial") {
    var manager = this;
    var $filtersList = jQuery('<span class="applied-filters-list">');
    this.appliedFiltersSummary.empty();

    if (this.searchFilter.isActive()) {
      var breadCrumbId = this.searchFilter.breadcrumbId(state);
      var $removeButton = manager.createRemoveButton();
      $removeButton.on("click", () => {
        manager.clearFilter(this.searchFilter, { breadcrumbId: breadCrumbId });
        manager.refreshPage();
      });

      var $matchAction = manager.createFilterActionElement(this.searchFilter.filterDescription(), "Show Search");
      $matchAction.on("click", () => this.searchFilter.$el.select());
      $filtersList.append(
        jQuery("<span>", { class: "filter-name", id: breadCrumbId }).append($matchAction).append($removeButton),
      );
    }

    const empty = !this.searchFilter.isActive();

    this.appliedFiltersSummary.append($filtersList).toggleClass("--empty", empty);
  }

  refreshTable() {
    if (this.wantsRemoteRefresh()) {
      this.refreshTableViaAjax();
    } else {
      this.refreshPage();
    }
  }

  reloadFromUrl() {
    window.location.reload();
  }

  wantsRemoteRefresh() {
    return !!this.el.data("remote");
  }

  refreshTableViaAjax() {
    jQuery.get({
      url: window.location.pathname,
      data: this.filterUrlParams(),
      dataType: "script",
      // Without `cache: true`, jQuery adds a cache-busting `_` param to a script request, and the response pushes the
      // request URL, `_` included, to the address bar. Admin responses already forbid caching
      // (`Concerns::HTTPCaching#prevent_caching`).
      cache: true,
      success: this.afterAjaxRefresh.bind(this),
    });
  }

  refreshPage() {
    window.loadingIndicator.show();
    window.location = window.location.pathname + "?" + this.filterUrlParams();
  }

  afterAjaxRefresh() {
    // Update bulk action return to URL
    jQuery(this.selectors.bulkSelectReturnToInput).val(window.location);

    // Re-init bulk selection checkboxes
    jQuery(document).trigger("bulkActionToolbar:reset");

    // Re-initialize sticky table headers
    document.dispatchEvent(new CustomEvent("indexTable:refreshed"));

    this.refreshControls();
    this.appliedFiltersSummary.show();
    this.refreshAppliedFiltersList();
  }

  filterUrlParams() {
    var url = new URL(window.location.href);
    var params = new URLSearchParams(url.search);

    // Drops jQuery's cache-busting `_` param, which a saved URL can carry, so the refresh does not send it and push it
    // back to the address bar.
    params.delete("_");
    this.resetFilterParams(params);

    if (this.focusControl.length) {
      params.delete(this.focusControlFilterName);
    }

    this.applyFilterParams(params);

    params.set("focus_mode", this.focusControl.is(":checked"));
    if (this.focusControl.is(":checked")) {
      params.set(this.focusControlFilterName, this.focusControlValue);
    }

    if (this.searchFilter.exists()) {
      params.set("query", this.searchFilter.$el.val());
    }

    if (params.get("page")) {
      params.delete("page");
    } // reset page params when a filter is changed

    return params.toString();
  }

  resetFilterParams(params) {
    if (this.searchFilter.exists()) {
      this.indexTableFilters.push(this.searchFilter);
    }

    var allParamNames = this.indexTableFilters.map(function () {
      return this.paramNames();
    });
    jQuery.each(allParamNames, function () {
      var paramWithoutArraySuffix = this.replace("[]", "");
      if (params.has(this)) {
        params.delete(this);
      } else if (params.has(paramWithoutArraySuffix)) {
        params.delete(paramWithoutArraySuffix);
      }
    });
  }

  applyFilterParams(params) {
    var appliedParams = this.activeFilters(this.indexTableFilters).map(function () {
      return this.urlParams();
    });

    jQuery.each(appliedParams, function () {
      jQuery.each(this, function (key, values) {
        jQuery.each([values].flat(), function (index, value) {
          params.append(key, value);
        });
      });
    });
  }

  clearFilter(filter, data) {
    if (data.hasOwnProperty("breadcrumbId")) {
      jQuery("#" + data.breadcrumbId).hide();
    }
    filter.clear();
    announceFilterChange(filter.$el[0], data);
  }

  activeFilters(filters) {
    return filters.filter(function () {
      return this.isActive();
    });
  }

  filters(container) {
    var manager = this;
    return container.find(this.selectors.filter).map(function () {
      return manager.indexFilter(this);
    });
  }

  indexFilter(input) {
    if (input.type == "hidden") {
      return new IndexTableHiddenFilter(input);
    } else if (input.type == "text") {
      return new IndexTableTextFilter(input);
    } else {
      return new IndexTableSelectFilter(input);
    }
  }

  createRemoveButton() {
    var removeButton = jQuery(createIconTag("close", { wrapper: { tag: "a", class: "filter-remove" } }));
    removeButton.attr({
      "data-tooltip": "Remove",
    });

    return removeButton;
  }

  createFilterActionElement(filterDescription, tooltip) {
    var matchAction = jQuery('<span class="filter-match-action">');
    matchAction.text(filterDescription); // Treat the filter description as text because it is user input and we don't want to treat it as HTML or we would open ourselves to XSS attacks
    matchAction.attr({
      "data-tooltip": tooltip,
    });

    return matchAction;
  }

  // ACCESSORS

  get indexTableFilters() {
    return this.primaryIndexFilter.add(this.sortMenuFilters);
  }

  get primaryIndexFilter() {
    return this.filters(this.primaryIndexFilterContainer);
  }

  get sortMenuFilters() {
    return this.filters(this.sortMenu);
  }

  get primaryIndexFilterContainer() {
    return this.el.find(this.selectors.primaryIndexFilterContainer);
  }

  get primaryIndexFilterInput() {
    return this.el.find(this.selectors.primaryIndexFilterInput);
  }

  get appliedFiltersSummary() {
    return this.el.find(this.selectors.appliedFiltersSummary);
  }

  get focusParamValue() {
    return new URL(window.location).searchParams.get(this.focusControlFilterName);
  }

  get focusControlCheckedValue() {
    return this.focusControl.data("focus-mode-param-value");
  }

  get focusControlValue() {
    return this.focusControl.is(":checked") ? this.focusControl.data("focus-mode-param-value") : "";
  }

  get focusControl() {
    return this.el.find(this.selectors.focusControl);
  }

  get focusControlFilterName() {
    return this.focusControl.data("focus-mode-param-name");
  }

  get searchInput() {
    return this.el.find(this.selectors.searchInput);
  }

  get sortInput() {
    return this.sortMenu.find(this.selectors.sortInput);
  }

  get sortDirectionInput() {
    return this.sortMenu.find(this.selectors.sortDirectionInput);
  }

  get sortDirectionContainer() {
    return this.sortMenu.find(this.selectors.sortDirectionContainer);
  }

  get sortText() {
    return this.sortMenu.find(this.selectors.sortText);
  }

  get sortMenu() {
    return this.el.find(this.selectors.sortMenu);
  }
}

class IndexTableFilter {
  constructor(el) {
    this.$el = jQuery(el);
    this.paramName = this.$el.attr("name");
  }

  // DSL

  isActive() {}
  clear() {}

  urlParams() {
    return { [this.paramName]: this.$el.val() };
  }

  paramNames() {
    return [this.$el.attr("name")];
  }

  filterDescription() {
    return [this.filterDescriptionLabel(), this.filterDescriptionText()].join(": ");
  }

  filterDescriptionText() {
    return this.$el.val();
  }

  filterDescriptionLabel() {
    return this.$el.data("filter-label");
  }

  exists() {
    return this.$el.length !== 0;
  }

  // Returns the id of the filter's breadcrumb in `state`, which the manager finds with an `#id` selector. The
  // brackets of a list parameter are dropped, and any other character a selector reads as syntax, such as the
  // `.` of `ratings.source`, becomes `_`.
  breadcrumbId(state) {
    return ["breadcrumb", state, this.paramName.replace(/[[\]]/g, "").replace(/[^\w-]/g, "_")].join("_");
  }
}

class IndexTableSelectFilter extends IndexTableFilter {
  constructor(el) {
    super(el);
    this.defaultValue = this.$el.data("index-table-default-value") || "";
  }

  // DSL

  isActive() {
    return isSelectFilterActive(this.$el[0]);
  }

  clear() {
    clearSelectFilter(this.$el[0], this.defaultValue);
  }

  filterDescriptionText() {
    return selectFilterDescription(this.$el[0]);
  }
}

class IndexTableTextFilter extends IndexTableFilter {
  constructor(el) {
    super(el);
  }

  isActive() {
    return this.exists() && !!this.$el.val().length;
  }

  clear() {
    return this.$el.val("");
  }

  filterDescriptionLabel() {
    return this.$el.data("filter-label") || this.$el.attr("type");
  }
}

class IndexTableHiddenFilter extends IndexTableFilter {
  constructor(el) {
    super(el);
  }

  // DSL

  isActive() {
    return !!this.$el.val();
  }

  clear() {
    return this.$el.val("");
  }

  filterDescription() {
    return this.$el.data("filter-label");
  }
}

// FILLING THE PAGE
//
// An index table's results (`data-behaviour="fill-available-height"`) are sized to the height left below them in the
// page, so they scroll instead of the page. An element ends where the nearest ancestor that cuts it off ends, or the
// window, leaving room for the elements that follow it in its parent and a gap (`data-fill-available-height-gap`,
// 20px by default). They are sized when the page loads, when the window is resized and when the table is refreshed.

const FILL_AVAILABLE_HEIGHT_SELECTOR = '[data-behaviour~="fill-available-height"]';
const DEFAULT_GAP = 20;

/**
 * Sets `element`'s height to the space below its top in the nearest ancestor that cuts it off, less the elements that
 * follow it in its parent and its gap, and never less than 0. The height is the same however far that ancestor is
 * scrolled.
 *
 * @param {HTMLElement} element an element marked `data-behaviour="fill-available-height"`
 */
export function fillAvailableHeight(element) {
  const bounding = boundingElement(element);
  element.style.height = ""; // the element can grow as well as shrink

  const bounds = element.getBoundingClientRect();
  const gap = Number(element.dataset.fillAvailableHeightGap ?? DEFAULT_GAP);
  const height = contentHeight(bounding) - topWithin(bounding, bounds) - gap - followingSiblingsHeight(element);

  element.style.height = `${Math.max(height, 0)}px`;
}

/**
 * Sizes every marked element in `root`.
 *
 * @param {ParentNode} [root=document]
 */
export function fillAvailableHeights(root = document) {
  root.querySelectorAll(FILL_AVAILABLE_HEIGHT_SELECTOR).forEach(fillAvailableHeight);
}

// STRETCH is a height far beyond any page, given to an element while its bounding element is found.
const STRETCH = "1000000px";

// Returns the nearest ancestor of `element` that cuts it off, or null when none does and the window bounds it. The
// element is stretched first, so an ancestor that limits its height cuts it off however little content it holds.
function boundingElement(element) {
  element.style.height = STRETCH;
  const bottom = element.getBoundingClientRect().bottom;

  for (let current = element.parentElement; current; current = current.parentElement) {
    if (current.getBoundingClientRect().bottom < bottom) {
      return current;
    }
  }

  return null;
}

// Returns how far `bounds` starts below the top of `element`'s scrolled content box, or of the page when `element` is
// null, so the result is the same however far either is scrolled.
function topWithin(element, bounds) {
  if (!element) {
    return bounds.top + window.scrollY;
  }

  const style = getComputedStyle(element);
  const contentTop = element.getBoundingClientRect().top + element.clientTop + parseFloat(style.paddingTop);
  return bounds.top - contentTop + element.scrollTop;
}

// Returns the height inside `element`'s padding and scrollbar, or the window's when `element` is null.
function contentHeight(element) {
  if (!element) {
    return document.documentElement.clientHeight;
  }

  const style = getComputedStyle(element);
  return element.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
}

function followingSiblingsHeight(element) {
  let height = 0;
  for (let sibling = element.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
    height += sibling.offsetHeight;
  }

  return height;
}

function handleIndexTableRefreshed() {
  fillAvailableHeights();
}

function handleWindowResize() {
  fillAvailableHeights();
}

function handleDocumentLoaded() {
  fillAvailableHeights();
}

document.addEventListener("indexTable:refreshed", handleIndexTableRefreshed);
window.addEventListener("resize", handleWindowResize);
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", handleDocumentLoaded);
} else {
  fillAvailableHeights();
}

window.IndexTableManager = IndexTableManager;
window.IndexTableFilter = IndexTableFilter;
window.IndexTableSelectFilter = IndexTableSelectFilter;
window.IndexTableTextFilter = IndexTableTextFilter;
window.IndexTableHiddenFilter = IndexTableHiddenFilter;
